import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { ArrowRight, MapPin, Star, ShieldAlert } from "lucide-react";
import { DISCLAIMER_TEXT } from "./Disclaimer";

export default function CompanyCareers() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/c/${encodeURIComponent(slug)}/branding`);
        setData(data);
      } catch (e) { setError(formatApiError(e.response?.data?.detail) || e.message); }
    })();
  }, [slug]);

  if (error) return (
    <div className="max-w-xl mx-auto px-6 py-20 text-center">
      <ShieldAlert className="w-10 h-10 mx-auto text-[#5C5C5C]" />
      <h1 className="font-display font-black text-3xl mt-5">Careers page not found.</h1>
      <p className="text-[#5C5C5C] mt-3" data-testid="careers-error">{error}</p>
      <Link to="/jobs" className="btn-ink mt-8 inline-flex">Browse all roles</Link>
    </div>
  );
  if (!data) return <div className="p-16 text-center text-[#5C5C5C]">Loading…</div>;

  const co = data.company;
  const platform = data.platform || {};
  const whitelabel = !!data.whitelabel;
  // Falls back to platform-wide brand when the company hasn't overridden.
  const brand = co.brand_primary_color || platform.primary_color || "#D94833";
  // When whitelabel is on, do NOT fall back to the platform's logo — only show the
  // company's own logo (or no logo at all if unset).
  const logoUrl = whitelabel ? (co.brand_logo_url || "") : (co.brand_logo_url || platform.logo_url || "");
  const heading = co.careers_display_name || `${co.name} Careers`;

  return (
    <div className="bg-[#FDFCF8] min-h-screen" style={{ "--brand-primary": brand, "--brand-primary-hover": brand }}>
      <header className="border-b border-[#E2E0D8] py-6">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            {logoUrl && (
              <img src={logoUrl} alt={co.name} className="w-10 h-10 object-contain" data-testid="careers-logo" />
            )}
            <div className="min-w-0">
              <div className="label-eyebrow truncate">{co.name}</div>
              <div className="font-display font-bold truncate">{heading}</div>
            </div>
          </div>
          {co.website && <a href={co.website} target="_blank" rel="noreferrer" className="text-sm font-semibold hover:opacity-70" style={{ color: brand }}>Visit site →</a>}
        </div>
      </header>

      <section className="max-w-[1100px] mx-auto px-6 lg:px-10 py-16">
        <div className="label-eyebrow" style={{ color: brand }}>Open roles · {data.jobs.length}</div>
        <h1 className="font-display font-black text-5xl lg:text-6xl tracking-tight mt-3" data-testid="careers-title">{heading}.</h1>
        {co.description && <p className="text-[#5C5C5C] mt-4 max-w-xl text-lg">{co.description}</p>}
        {co.org_type === "game" && (
          <div data-testid="careers-game-disclaimer" className="mt-6 border border-purple-300 bg-purple-50 px-4 py-3 text-sm text-purple-900 max-w-2xl">
            <strong className="font-display">Game group.</strong> This careers page belongs to an online community / game. <strong>Not affiliated with any official organisation.</strong> Listings on this page are not real paid vacancies.
          </div>
        )}

        <div className="mt-12 grid gap-px bg-[#E2E0D8] border border-[#E2E0D8]" data-testid="careers-jobs">
          {data.jobs.length === 0 && (
            <div className="bg-[#FDFCF8] p-10 text-center text-[#5C5C5C]">
              No open roles right now — check back soon.
            </div>
          )}
          {data.jobs.map((j) => (
            <Link to={`/jobs/${j.job_id}`} key={j.job_id} data-testid={`careers-job-${j.job_id}`} className="bg-[#FDFCF8] p-6 hover:bg-[#0F0F0F]/5 transition-colors">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    {j.tier === "premium" && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold px-1.5 py-0.5" style={{ background: brand, color: "white" }}><Star className="w-2.5 h-2.5" /> Spotlight</span>}
                    {j.tier === "featured" && <span className="text-[10px] uppercase tracking-widest font-semibold px-1.5 py-0.5 border" style={{ color: brand, borderColor: brand }}>Featured</span>}
                  </div>
                  <div className="font-display font-black text-2xl mt-2 truncate">{j.title}</div>
                  <div className="text-sm text-[#5C5C5C] mt-1 flex items-center gap-3 flex-wrap">
                    <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {j.location}</span>
                    {j.employment_type && <span>· {j.employment_type}</span>}
                    {j.salary_range && <span>· {j.salary_range}</span>}
                  </div>
                </div>
                <ArrowRight className="w-5 h-5 flex-shrink-0" style={{ color: brand }} />
              </div>
            </Link>
          ))}
        </div>
      </section>

      <footer className="border-t border-[#E2E0D8] py-10" data-testid="careers-footer">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10 space-y-4 text-xs text-[#5C5C5C]">
          {!whitelabel && (
            <p data-testid="careers-powered-by">
              Powered by <Link to="/" className="font-bold underline hover:opacity-70" style={{ color: brand }}>Ocean Ash</Link>.
            </p>
          )}
          {whitelabel ? (
            <p className="leading-relaxed">© {new Date().getFullYear()} {co.name}. All roles published here are managed directly by {co.name}.</p>
          ) : (
            <p className="leading-relaxed">{DISCLAIMER_TEXT.slice(0, 280)}… <Link className="underline" to="/disclaimer">read full disclaimer</Link></p>
          )}
        </div>
      </footer>
    </div>
  );
}
